import GlobalContext from './GlobalContext';
import { validatePassword } from '../util/validation/passwordValidation';
import { useLocation } from 'react-router';
import API from '../api/api';
import { TResetPasswordPost } from '../api/types';
import { EQueryStringParam, EStaffAccountType } from '../util/constants';
import { createContext, FC, useContext, useEffect, useState } from 'react';

type TResetPasswordStaffContext = {
    resetPasswordStaffUiState: EResetPasswordStaffUiState;
    setResetPasswordStaffUiState: (state: EResetPasswordStaffUiState) => void;
    password: string;
    setPassword: (password: string) => void;
    passwordIsValid: boolean;
    setPasswordHasFocus: (passwordHasFocus: boolean) => void;
    setPasswordIsValid: (passwordIsValid: boolean) => void;
    setPasswordHasValidated: (passwordHasValidated: boolean) => void;
    password2: string;
    setPassword2: (password2: string) => void;
    password2IsValid: boolean;
    setPassword2HasFocus: (password2HasFocus: boolean) => void;
    setPassword2IsValid: (password2IsValid: boolean) => void;
    setPassword2HasValidated: (password2HasValidated: boolean) => void;
    password2ValidationMessage: string;
    setPassword2ValidationMessage: (password2ValidationMessage: string) => void;
    showValidationCriteria: boolean;
    setShowValidationCriteria: (showValidation: boolean) => void;
    validationIsActive: boolean;
    setValidationIsActive: (validationIsActive: boolean) => void;
    disableSubmit: boolean;
    userID: string;
    userToken: string;
    customerAccountId: string;
    submitResetPasswordForm: () => void;
};

export enum EResetPasswordStaffUiState {
    default,
    loading,
    success,
    expired,
    error,
    fatalError,
}

const ResetPasswordStaffContext = createContext<TResetPasswordStaffContext | undefined>(undefined);

export const ResetPasswordStaffProvider: FC = ({ children }) => {
    const { language, gatewayAPIBaseUrl, setStaffAccountType } = useContext(GlobalContext);
    const [resetPasswordStaffUiState, setResetPasswordStaffUiState] = useState(EResetPasswordStaffUiState.default);
    const [userID, setUserID] = useState('');
    const [userToken, setUserToken] = useState('');
    const [customerAccountId, setCustomerAccountId] = useState('');
    const [password, setPassword] = useState('');
    const [passwordIsValid, setPasswordIsValid] = useState(true);
    const [passwordHasValidated, setPasswordHasValidated] = useState(false);
    const [passwordHasFocus, setPasswordHasFocus] = useState(false);
    const [password2, setPassword2] = useState('');
    const [password2IsValid, setPassword2IsValid] = useState(true);
    const [password2HasValidated, setPassword2HasValidated] = useState(false);
    const [password2HasFocus, setPassword2HasFocus] = useState(false);
    const [password2ValidationMessage, setPassword2ValidationMessage] = useState('');
    const [showValidationCriteria, setShowValidationCriteria] = useState(false);
    const [validationIsActive, setValidationIsActive] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(true);
    const { search: queryParams } = useLocation();

    const getQueryParams = () => {
        const params = new URLSearchParams(queryParams);
        const userId = params.get(EQueryStringParam.userId);
        const code = params.get(EQueryStringParam.code);
        const customerAccountId = params.get(EQueryStringParam.customerAccountId);
        if (userId && code && customerAccountId) {
            setUserID(userId);
            setUserToken(code);
            setCustomerAccountId(customerAccountId);
        } else {
            setResetPasswordStaffUiState(EResetPasswordStaffUiState.fatalError);
        }
    };

    const checkDisableSubmit = () => {
        if (password.length && passwordIsValid && password === password2) {
            setDisableSubmit(false);
        } else {
            if (disableSubmit === false) {
                setDisableSubmit(true);
            }
        }
    };

    const checkPasswordIsValid = () => {
        if (passwordHasValidated) {
            if (validatePassword(password)) {
                setPasswordIsValid(true);
            } else {
                if (passwordIsValid === true) {
                    setPasswordIsValid(false);
                }
            }
        }
    };

    const checkPassword2IsValid = () => {
        if (password2HasValidated) {
            if (validatePassword(password2) && password === password2) {
                setPassword2IsValid(true);
            } else {
                if (password2IsValid === true) {
                    setPassword2IsValid(false);
                }
                if (password2 !== password) {
                    setPassword2ValidationMessage(language.resetPassword.passwordsDontMatch);
                } else {
                    setPassword2ValidationMessage(undefined);
                }
            }
        }
    };

    const submitResetPasswordForm = async () => {
        const postObject: TResetPasswordPost = {
            password,
            token: userToken,
        };
        try {
            setResetPasswordStaffUiState(EResetPasswordStaffUiState.loading);
            await API.post.resetPasswordStaff(gatewayAPIBaseUrl, postObject, userID);
            setResetPasswordStaffUiState(EResetPasswordStaffUiState.success);
        } catch (error) {
            if (error.response && error.response.status === 400) {
                setResetPasswordStaffUiState(EResetPasswordStaffUiState.expired);
            } else {
                setResetPasswordStaffUiState(EResetPasswordStaffUiState.error);
            }
        }
    };

    useEffect(() => {
        setStaffAccountType(EStaffAccountType.account);
        getQueryParams();
    }, []);

    useEffect(() => {
        password.length > 0 && setValidationIsActive(true);
        checkPasswordIsValid();
        checkPassword2IsValid();
        checkDisableSubmit();
    }, [password, password2, passwordHasFocus, password2HasFocus]);

    return (
        <ResetPasswordStaffContext.Provider
            value={{
                resetPasswordStaffUiState,
                password,
                setPassword,
                passwordIsValid,
                setPasswordIsValid,
                setPasswordHasFocus,
                setPasswordHasValidated,
                password2,
                setPassword2,
                password2IsValid,
                setPassword2IsValid,
                setPassword2HasFocus,
                setPassword2HasValidated,
                password2ValidationMessage,
                setPassword2ValidationMessage,
                setResetPasswordStaffUiState,
                showValidationCriteria,
                setShowValidationCriteria,
                validationIsActive,
                setValidationIsActive,
                disableSubmit,
                userID,
                userToken,
                customerAccountId,
                submitResetPasswordForm,
            }}
        >
            {children}
        </ResetPasswordStaffContext.Provider>
    );
};

export default ResetPasswordStaffContext;
